import React, { useState, useRef } from "react";
import styles from "./GoalsHeader.module.scss";
import Icon from "../../../common/Icon/Icon";
import HTMLtoText from "../../../common/HTMLToText/HTMLToText";
import { useAppSelector } from "../../../../redux/hooks";
import useClickOutside from "../../../CustomHooks/useClickOutside";
import { useTranslation } from "react-i18next";
import { StatusDropdown } from "../../../common/StatusDropdown/StatusDropdown";
import Modal from "../../../common/Modal/Modal";
import AddGoalModal from "../Goals/AddGoalModal/AddGoalModal";
import { convertUrlsToLinks, useProfileParams } from "../../../../utils/common";
import {
  getGoalStatusLabel,
  getIncomeTypeLabel,
  getTimeFrameTypeLabel,
} from "../../../../utils/goalUtil";
import { getAwardNameFromCode } from "../../../../utils/awardIconMapping";
import { CurrencyFormatter } from "../../../../utils/currencyFormatter";
import { useUpdateGoalStatusMutation } from "../../../../redux/services/goalsAPI";
import { isEmpty } from "lodash";
import { GOAL_STATUS } from "../../../../constants/dataConstants";

type GoalHeaderProps = {
  goalData: GoalData | HistoryItem;
  selectedItem: string;
  setSelectedItem: (item: string) => void;
  isDetailsPage?: boolean;
  hasLeader: boolean;
  onGoalSave?: (goalData: GoalData) => void;
  selectedStatusItem: { [key: string]: string };
  setSelectedStatusItem: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >;
  isHistoryGoalData?: boolean;
};

const GoalHeader = ({
  goalData,
  selectedItem,
  setSelectedItem,
  isDetailsPage,
  hasLeader,
  onGoalSave,
  selectedStatusItem,
  setSelectedStatusItem,
  isHistoryGoalData,
}: GoalHeaderProps) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const currencyCode = useAppSelector(
    (state: any) => state.currency.currencyCode
  );
  const { affiliateCode, aboNumber } = useProfileParams();

  const [selectedStatus, setSelectedStatus] = useState(
    getGoalStatusLabel(goalData?.status) || "On Track"
  );

  const [
    updateGoalsStatus,
    { isLoading: isLoadingUpdateGoalstatus, isError: isErrorInSaveStatus },
  ] = useUpdateGoalStatusMutation();

  const handleClickAddGoal = () => {
    setGoalModalOpen(true);
  };

  const handleGoalModalClose = () => {
    setGoalModalOpen(false);
  };

  // useClickOutside(dropdownRef, () => setShowDropdown(false));

  const key = String(goalData.aboGoalId ?? "");

  return (
    <div className={`${styles.goals_status_wrapper}`}>
      <div className={styles.heading_wrapper}>
        <div
          className={`${styles.heading_area} ${isDetailsPage ? styles.isDetails : ""}`}
        >
          {goalData?.title}
        </div>
        {isDetailsPage && !isHistoryGoalData && (
          <div className={styles.edit_goal} onClick={handleClickAddGoal}>
            <Icon
              name="edit_goal_icon"
              size={14}
              className={styles.edit_goal_icon}
              color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
            />
            <span className={styles.edit_text}>{t("edit_goal")}</span>
          </div>
        )}

        {!isDetailsPage && hasLeader && (
          <div className={styles.leader_action}>
            <Icon
              name="ellipsis_icon"
              size={16}
              className={styles.green_icon}
              color={currentTheme === "light" ? "#324C48" : "#324C48"}
            />
            <span className={styles.leader_action_text}>
              {t("leader_action")}
            </span>
          </div>
        )}
      </div>

      <div className={styles.status_wrapper} 
      >
        <StatusDropdown
          options={GOAL_STATUS}
          selectedItem={getGoalStatusLabel(goalData?.status) || selectedStatus}
          setSelectedItem={(item, itemCode) => {
            updateGoalsStatus({
              affiliateCode,
              aboNumber,
              goalId: key,
              statusId: itemCode,
            }).then((resp: any) => {
              const updateStatusRestData = resp?.data?.body;
              if (!isEmpty(updateStatusRestData)) {
                onGoalSave?.(updateStatusRestData);
                setSelectedStatusItem((prev) => ({
                  ...prev,
                  [key]: item,
                }));
              }
            });
          }}
        />

        <div
          className={`qualification_wrapper ${styles.qualification_wrapper}`}
        >
          <span className={`qualification_text ${styles.qualification_text}`}>
            {goalData?.outcomeDetail?.awardCode ? (
              <span>
                Qualification -{" "}
                {getAwardNameFromCode(
                  goalData?.outcomeDetail?.awardCode?.toString(),
                  t
                ) || "N/A"}
              </span>
            ) : (
              <span>
                {getIncomeTypeLabel(goalData?.outcomeDetail?.incomeType)} -{" "}
                {goalData?.outcomeDetail?.incomeValue
                  ? CurrencyFormatter(
                      currencyCode,
                      Number(goalData?.outcomeDetail?.incomeValue ?? 0)
                    )
                  : "N/A"}
              </span>
            )}
          </span>
        </div>

        {goalData?.timeframeType && (
          <>
            <div className={styles.calendar_wrapper}>
              <Icon
                name="calendar"
                size={14}
                className={styles.calendar_icon}
                color={currentTheme === "light" ? "#707070" : "#AEAEB2"}
              />
              <span className={styles.PY_value}>
                {getTimeFrameTypeLabel(goalData.timeframeType)}
              </span>
            </div>
            <span className={styles.seperator_icon}>
              <Icon
                name="seperator_icon"
                size={14}
                className={styles.seperator_icon}
                color={currentTheme === "light" ? "#707070" : "#AEAEB2"}
              />
            </span>
          </>
        )}
        <span className={styles.date_created}>Just now</span>
      </div>

      <div
        className={`${styles.goals_desc} ${isDetailsPage ? styles.details_desc : ""}`}
      >
        <div className={styles.desc_text}>
          <HTMLtoText
            htmlContent={convertUrlsToLinks(goalData?.note || "", currentTheme)}
            alternateMessage=""
          />
        </div>
        {isDetailsPage && hasLeader && (
          <div className={styles.leader_action}>
            <Icon
              name="ellipsis_icon"
              size={16}
              className={styles.green_icon}
              color={currentTheme === "light" ? "#324C48" : "#324C48"}
            />
            <span className={styles.leader_action_text}>
              {t("leader_action")}
            </span>
          </div>
        )}
      </div>

      <Modal showDrawer={goalModalOpen} onClose={handleGoalModalClose}>
        <AddGoalModal
          initialGoal={goalData}
          onClose={handleGoalModalClose}
          onSave={(updatedGoal) => {
            const goalPayload = {
              aboGoalId: updatedGoal?.aboGoalId,
              title: updatedGoal?.goalTitle,
              note: updatedGoal?.note,
              timeframeType: updatedGoal.timeFrame,
              kpiCodes:
                updatedGoal.kpiCodes.map((metric: any) => metric.code) || [],
              outcomeType: updatedGoal?.outcome,
              outcomeDetail: {
                incomeType: updatedGoal?.incomeType,
                incomeValue: +updatedGoal?.monthlyIncome,
                awardCode: updatedGoal?.awardType?.code,
                other:
                  updatedGoal?.outcome === "9" || updatedGoal?.outcome === 9
                    ? "other description"
                    : "",
              },
              // completionDate: updatedGoal?.completionDate,
              // status: updatedGoal?.status,
              // archive: updatedGoal?.archive
            };
            onGoalSave?.(goalPayload as GoalData);
            setGoalModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default GoalHeader;
